import { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Polygon, Marker, useMapEvents, useMap } from "react-leaflet";
import { X, Save, Undo2, RotateCcw, Trash2, Info, MapPin, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { DRAW_LAYER, MAX_ZOOM } from "@/lib/mapLayers";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const markerIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

function MapClickHandler({ onMapClick, enabled }) {
  useMapEvents({
    click: (e) => { if (enabled) onMapClick(e.latlng); },
  });
  return null;
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 17, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

// Formule Shoelace pour surface (approximation en hectares depuis lat/lon)
function polygonAreaHa(points) {
  if (points.length < 3) return 0;
  const R = 6371000; // rayon Terre en m
  const toRad = (d) => (d * Math.PI) / 180;
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    area +=
      toRad(p2.lng - p1.lng) *
      (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
  }
  area = Math.abs((area * R * R) / 2);
  return area / 10000;
}

export default function RecalibrateParcelModal({ parcel, onClose, onSaved }) {
  // Extraire les points d'origine depuis la géométrie de la parcelle
  const originalPoints = useMemo(() => {
    const g = parcel?.geometry;
    if (!g?.coordinates?.length) return [];
    const t = String(g.type || "").toLowerCase();
    let ring = null;
    if (t === "polygon") ring = g.coordinates[0];
    else if (t === "multipolygon") ring = g.coordinates[0]?.[0];
    if (!ring) return [];
    // Enlève le dernier point qui ferme le polygone (identique au 1er)
    const arr = ring.slice(0, ring[0]?.[0] === ring[ring.length - 1]?.[0] && ring[0]?.[1] === ring[ring.length - 1]?.[1] ? -1 : undefined);
    return arr.map(([lon, lat]) => ({ lat, lng: lon }));
  }, [parcel]);

  const centroid = useMemo(() => {
    if (!originalPoints.length) return [parcel?.centroid_lat || -21.1, parcel?.centroid_lon || 55.5];
    const sLat = originalPoints.reduce((s, p) => s + p.lat, 0) / originalPoints.length;
    const sLng = originalPoints.reduce((s, p) => s + p.lng, 0) / originalPoints.length;
    return [sLat, sLng];
  }, [originalPoints, parcel]);

  const [points, setPoints] = useState([]); // nouveaux points recalibrés
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [flyTarget] = useState({ center: centroid, zoom: 17 });

  const addPoint = (latlng) => {
    setPoints((prev) => [...prev, { lat: latlng.lat, lng: latlng.lng }]);
  };

  const removeLastPoint = () => {
    setPoints((prev) => prev.slice(0, -1));
  };

  const clearAll = () => {
    setPoints([]);
  };

  const startFromOriginal = () => {
    setPoints([...originalPoints]);
    toast.info("Points d'origine copiés — déplacez ou ajoutez des points pour ajuster");
  };

  const newSurfaceHa = useMemo(() => polygonAreaHa(points), [points]);
  const originalSurfaceHa = parcel?.surface_ha || 0;
  const deltaPercent = originalSurfaceHa > 0 ? Math.round(((newSurfaceHa - originalSurfaceHa) / originalSurfaceHa) * 100) : 0;

  const save = async () => {
    if (points.length < 3) {
      toast.error("Ajoutez au moins 3 points sur la carte pour définir le nouveau contour");
      return;
    }
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error("Motif de recalibration requis (5 caractères minimum)");
      return;
    }
    setSaving(true);
    try {
      const body = {
        recalibrate: true,
        reason: reason.trim(),
        points: points.map((p) => ({ lat: p.lat, lon: p.lng })),
      };
      const res = await authFetch(`${API}/parcels/${parcel.parcel_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || "Erreur lors de la recalibration");
        setSaving(false);
        return;
      }
      const updated = await res.json();
      toast.success(`Parcelle recalibrée — nouvelle surface : ${updated.surface_ha?.toFixed(2)} ha`);
      onSaved?.(updated);
    } catch (e) {
      toast.error("Erreur réseau");
    }
    setSaving(false);
  };

  const resetToOriginal = async () => {
    if (!parcel?.has_original_geometry && !parcel?.original_geometry_backup) {
      toast.error("Aucune géométrie d'origine sauvegardée pour cette parcelle");
      return;
    }
    if (!window.confirm("Restaurer la géométrie d'origine ? La recalibration actuelle sera supprimée.")) return;
    setSaving(true);
    const res = await authFetch(`${API}/parcels/${parcel.parcel_id}/recalibration/reset`, { method: "POST" });
    setSaving(false);
    if (res.ok) {
      toast.success("Géométrie d'origine restaurée");
      onSaved?.();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err?.detail || "Erreur");
    }
  };

  return (
    <div className="fixed inset-0 z-[3000] bg-black/70 flex items-center justify-center p-2" data-testid="recalibrate-modal">
      <div className="bg-white rounded-xl w-full max-w-6xl h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-amber-600 to-orange-600 text-white shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-widest opacity-90">Recalibration manuelle</div>
            <div className="text-base font-bold">{parcel.name || parcel.parcel_id}</div>
          </div>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/20" onClick={onClose} data-testid="close-recalibrate-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Instructions bar */}
        <div className="px-4 py-2 border-b bg-amber-50 text-[12px] text-amber-900 flex items-start gap-2 shrink-0">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            <b>Cliquez sur la carte</b> pour ajouter les sommets du nouveau contour (minimum 3 points).
            La géométrie d'origine (contour rouge) est conservée comme sauvegarde et peut être restaurée à tout moment.
          </p>
        </div>

        {/* Body : map + sidebar */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* Map */}
          <div className="flex-1 min-h-[280px] relative">
            <MapContainer
              center={centroid}
              zoom={17}
              maxZoom={MAX_ZOOM}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
              data-testid="recalibrate-map"
            >
              <TileLayer url={DRAW_LAYER.url} attribution={DRAW_LAYER.attribution} maxZoom={MAX_ZOOM} maxNativeZoom={DRAW_LAYER.maxNativeZoom} />
              <FlyTo center={flyTarget.center} zoom={flyTarget.zoom} />
              <MapClickHandler onMapClick={addPoint} enabled={!saving} />

              {/* Polygone d'origine (rouge, si affiché) */}
              {showOriginal && originalPoints.length >= 3 && (
                <Polygon
                  positions={originalPoints.map((p) => [p.lat, p.lng])}
                  pathOptions={{ color: "#DC2626", weight: 2, fillColor: "#DC2626", fillOpacity: 0.15, dashArray: "6, 4" }}
                />
              )}

              {/* Nouveau polygone recalibré (vert) */}
              {points.length >= 3 && (
                <Polygon
                  positions={points.map((p) => [p.lat, p.lng])}
                  pathOptions={{ color: "#059669", weight: 3, fillColor: "#059669", fillOpacity: 0.3 }}
                />
              )}
              {points.map((p, i) => (
                <Marker key={i} position={[p.lat, p.lng]} icon={markerIcon} data-testid={`recalibrate-point-${i}`} />
              ))}
            </MapContainer>

            {/* Overlay compteur points */}
            {points.length > 0 && (
              <div className="absolute top-3 left-3 bg-white rounded-lg shadow-md px-3 py-1.5 text-xs font-semibold text-emerald-800 flex items-center gap-2" data-testid="points-counter">
                <MapPin className="w-3.5 h-3.5" /> {points.length} point{points.length > 1 ? "s" : ""}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="md:w-80 border-t md:border-t-0 md:border-l bg-gray-50 flex flex-col overflow-y-auto shrink-0 max-h-[45vh] md:max-h-none">
            <div className="p-3 space-y-3">
              {/* Actions de dessin */}
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-gray-600">Dessin</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={removeLastPoint} disabled={points.length === 0} data-testid="undo-point-btn">
                    <Undo2 className="w-3 h-3 mr-1" />Annuler
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={clearAll} disabled={points.length === 0} data-testid="clear-points-btn">
                    <Trash2 className="w-3 h-3 mr-1" />Effacer
                  </Button>
                </div>
                <Button size="sm" variant="outline" className="w-full h-8 text-[11px]" onClick={startFromOriginal} disabled={originalPoints.length < 3} data-testid="copy-original-btn">
                  Partir du contour d'origine
                </Button>
                <Button size="sm" variant="ghost" className="w-full h-7 text-[11px] text-gray-600" onClick={() => setShowOriginal((v) => !v)} data-testid="toggle-original-btn">
                  {showOriginal ? "Masquer" : "Afficher"} le contour d'origine
                </Button>
              </div>

              {/* Comparaison surfaces */}
              <div className="p-2.5 rounded-lg border border-gray-200 bg-white space-y-1 text-[11px]">
                <div className="flex justify-between">
                  <span className="text-gray-500">Surface d'origine :</span>
                  <span className="font-semibold text-gray-800">{originalSurfaceHa?.toFixed(2)} ha</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Nouvelle surface :</span>
                  <span className="font-semibold text-emerald-700" data-testid="new-surface">
                    {points.length >= 3 ? `${newSurfaceHa.toFixed(2)} ha` : "—"}
                  </span>
                </div>
                {points.length >= 3 && originalSurfaceHa > 0 && (
                  <div className={`flex justify-between pt-1 border-t border-gray-100 ${Math.abs(deltaPercent) > 20 ? "text-amber-700" : "text-gray-600"}`}>
                    <span>Écart :</span>
                    <span className="font-semibold" data-testid="delta-percent">{deltaPercent > 0 ? "+" : ""}{deltaPercent} %</span>
                  </div>
                )}
                {Math.abs(deltaPercent) > 30 && (
                  <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5 mt-1 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>Écart supérieur à 30 %. Vérifiez le tracé — les données agronomiques (AMENDA, pluvio) seront recalculées.</span>
                  </div>
                )}
              </div>

              {/* Motif */}
              <div>
                <p className="text-[10px] uppercase font-semibold text-gray-600 mb-1">Motif de recalibration *</p>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex : parcelle mal détectée par RPG, contour décalé de 15m au nord…"
                  className="text-xs bg-white"
                  rows={3}
                  data-testid="recalibrate-reason"
                />
              </div>

              {/* Reset backup */}
              {(parcel?.has_original_geometry || parcel?.original_geometry_backup) && (
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-[10px] uppercase font-semibold text-gray-600 mb-1">Sauvegarde</p>
                  <p className="text-[11px] text-gray-600 mb-1.5">Cette parcelle a déjà été recalibrée. Vous pouvez restaurer la géométrie d'origine.</p>
                  <Button size="sm" variant="outline" className="w-full h-8 text-[11px] border-red-300 text-red-700 hover:bg-red-50" onClick={resetToOriginal} disabled={saving} data-testid="reset-original-btn">
                    <RotateCcw className="w-3 h-3 mr-1" />Restaurer l'origine
                  </Button>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="mt-auto p-3 border-t bg-white space-y-1.5">
              <Button
                size="sm"
                className="w-full h-9 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                onClick={save}
                disabled={saving || points.length < 3 || reason.trim().length < 5}
                data-testid="save-recalibration-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {saving ? "Enregistrement…" : "Enregistrer la recalibration"}
              </Button>
              <Button size="sm" variant="ghost" className="w-full h-8 text-xs text-gray-600" onClick={onClose} data-testid="cancel-recalibrate-btn">
                Annuler
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
